/** The lab's queues and actions (admin_handler's lab.rs). Admin only. */
import { api } from './client';
import type { NegativesPlan, Roll, RollFormat, RollKind, RollProcess } from '../types/roll';

/** A roll as the lab queue lists it. */
export interface LabRoll {
  order_id: string;
  order_number: string | null;
  customer_id: string | null;
  customer_name: string | null;
  roll_id: string;
  roll_name: string;
  lab_roll_number: string | null;
  kind: RollKind;
  format: RollFormat | null;
  process: RollProcess | null;
  film_brand: string | null;
  film_stock_name: string | null;
  special_instructions: string | null;
  negatives_plan: NegativesPlan | null;
  /** When it joined the queue: received, developed, or the end of its hold. */
  since: string | null;
  held_until?: string;
  /** A hold that has run out: the negatives are to be disposed of. */
  overdue: boolean;
  reminder_sent_at?: string;
  /** When the lab finished scanning it: scans confirmed, or marked empty. */
  scanned_at?: string;
  /** Scanned by being marked empty: nothing came out. */
  marked_empty: boolean;
}

/** An order's rolls in the scan queue. */
export interface LabOrderGroup {
  order_id: string;
  order_number: string | null;
  customer_name: string | null;
  rolls: LabRoll[];
}

export interface LabCounts {
  develop: number;
  /** Rolls still to scan. */
  scan: number;
  /** Orders scanned and waiting to be marked ready. */
  ready: number;
  held: number;
  overdue: number;
}

export interface LabQueue {
  /** A group per process, in tank order (C-41, B&W, E-6, other, unknown). */
  develop: { process: RollProcess | null; rolls: LabRoll[] }[];
  /** Rolls still to scan, a group per order, the one waiting longest first. */
  scan: LabOrderGroup[];
  /**
   * Orders with every roll scanned and none still to develop, to be marked
   * ready: the one scanned longest ago first.
   */
  ready: LabOrderGroup[];
  /** Soonest to end first. */
  held: LabRoll[];
  counts: LabCounts;
}

export type DevelopedOutcome = 'developed' | 'skipped' | 'changed' | 'not_found' | 'failed';

/** What happened to a roll's negatives, as the technician records it. */
export type NegativesActionRequest =
  | { action: 'posted_back'; tracking?: { carrier?: string; number?: string; url?: string } }
  | { action: 'collected' }
  | { action: 'disposed' }
  | { action: 'returned_with_order'; returned_with_order_id: string }
  | { action: 'reopen' };

export interface RollByNumber {
  order_id: string;
  order_number: string;
  customer_name: string | null;
  claimed_at: string;
  roll: Roll;
}

export interface AdoptedHold {
  order_id: string;
  order_number: string;
  roll_id: string;
  roll_name: string;
  held_until: string;
  outcome: 'would_adopt' | 'adopted' | 'changed' | 'failed';
}

const rollPath = (orderId: string, rollId: string) =>
  `/api/v1/admin/orders/${encodeURIComponent(orderId)}/rolls/${encodeURIComponent(rollId)}`;

export const lab = {
  queue: () => api.get<LabQueue>('/api/v1/admin/lab/queue'),
  developed: (rolls: { order_id: string; roll_id: string }[]) =>
    api.post<{ results: { order_id: string; roll_id: string; outcome: DevelopedOutcome }[] }>('/api/v1/admin/lab/developed', {
      rolls,
    }),
  setStage: (orderId: string, rollId: string, stage: 'developed' | 'received') =>
    api.put<{ roll: Roll }>(`${rollPath(orderId, rollId)}/stage`, { stage }).then((r) => r.roll),
  negatives: (orderId: string, rollId: string, request: NegativesActionRequest) =>
    api.post<{ roll: Roll }>(`${rollPath(orderId, rollId)}/negatives`, request).then((r) => r.roll),
  byNumber: (number: string) =>
    api.get<{ number: string; rolls: RollByNumber[] }>(`/api/v1/admin/lab/rolls/by-number/${encodeURIComponent(number)}`),
  adoptLegacy: (dryRun: boolean) =>
    api.post<{ dry_run: boolean; rolls: AdoptedHold[]; orders_without_rolls: string[] }>('/api/v1/admin/lab/adopt-legacy', {
      dry_run: dryRun,
    }),
};
